import { type RowWindow } from "../gantt-layout.ts"
import { type AxisUnit, axisUnitFor, resolveDayWidth } from "../gantt-scale.ts"
import {
  AVAILABLE_ROW_HEIGHT_PT,
  IMAGE_GAP_PT,
  PDF_CHART_WIDTH_PX,
  USABLE_WIDTH_PT
} from "./gantt-pdf-constants.ts"

export interface GanttPdfPlan {
  dayWidth: number
  axisUnit: AxisUnit
  pages: RowWindow[]
}

/**
 * The PDF draws the whole span on every page, on the same Fit scale and axis
 * rules as the screen, and splits the rows into as many pages as it takes
 * for each to fit A4 at the scale the page width sets.
 */
export function planGanttPdfPages({
  totalDays,
  rowCount,
  leftPanelWidth,
  headerHeight,
  rowHeight,
  maxDayWidth
}: {
  totalDays: number
  rowCount: number
  leftPanelWidth: number
  headerHeight: number
  rowHeight: number
  maxDayWidth: number
}): GanttPdfPlan {
  const dayWidth = resolveDayWidth(
    "fit",
    totalDays,
    PDF_CHART_WIDTH_PX,
    maxDayWidth
  )
  const scale = Math.min(
    1,
    (USABLE_WIDTH_PT - IMAGE_GAP_PT) / (leftPanelWidth + totalDays * dayWidth)
  )
  const rowsPerPage = Math.max(
    1,
    Math.floor((AVAILABLE_ROW_HEIGHT_PT / scale - headerHeight) / rowHeight)
  )
  const pages: RowWindow[] = []
  for (let first = 0; first < rowCount; first += rowsPerPage) {
    pages.push({ first, last: Math.min(first + rowsPerPage, rowCount) - 1 })
  }
  return {
    dayWidth,
    axisUnit: axisUnitFor(dayWidth),
    pages: pages.length ? pages : [{ first: 0, last: -1 }]
  }
}
